import { apiClient, toUnixSeconds } from '@/shared/api';
import type { AuthTokens } from '@/shared/api';

import type {
  AuthTokensResponse,
  ForgotPasswordResponse,
  LoginResponse,
  ProfileResponse,
  RegisterResponse,
  StepUpAction,
  StepUpResponse,
  TotpConfirmedResponse,
  TotpSetupResponse
} from './types';

/**
 * Every auth endpoint, in one place.
 *
 * Notes that matter:
 *
 *  - `skipAuth: true` on the unauthenticated calls. Without it the client would
 *    attach whatever stale access token happens to be in memory, and a 401 from
 *    /login would trigger a pointless refresh round-trip.
 *  - `/auth/2fa/verify` takes the pre-auth token in the BODY, not as a Bearer
 *    header. That is what the backend's `VerifyTwoFactorRequest` declares, and
 *    it is the safer shape anyway: the pre-auth token never touches the
 *    Authorization header, so it can never be picked up by the generic
 *    interceptor and replayed against a real endpoint.
 *  - Nothing here logs. Codes, tokens and secrets pass through this module.
 */

const BASE = '/api/v1/auth';
const ACCOUNT = '/api/v1/account';

/**
 * Options for the authenticated endpoints that VERIFY A TOTP CODE.
 *
 * These answer 401 for `invalid_2fa_code` and `2fa_code_already_used` — failures
 * of the *code*, not of the session. Without `skipRefresh` the client would read
 * that 401 as "access token stale", fire a refresh, rotate the refresh token and
 * replay the request with the same already-spent code. The user would see a
 * mistyped digit turn into a rotated session, and a second mistype could revoke
 * the family outright. The session is fine here; only the code was wrong.
 */
const CODE_ENDPOINT = { skipRefresh: true } as const;

export const authApi = {
  register: (body: { email: string; password: string }) =>
    apiClient.post<RegisterResponse>(`${BASE}/register`, body, { skipAuth: true }),

  confirmEmail: (body: { token: string }) =>
    apiClient.post<null>(`${BASE}/confirm-email`, body, { skipAuth: true }),

  login: (body: { email: string; password: string }) =>
    apiClient.post<LoginResponse>(`${BASE}/login`, body, { skipAuth: true }),

  verifyTwoFactor: (body: { preAuthToken: string; code: string }) =>
    apiClient.post<AuthTokensResponse>(`${BASE}/2fa/verify`, body, { skipAuth: true }),

  /**
   * Ends the session server-side.
   *
   * Required now that the refresh token lives in an httpOnly cookie: the SPA
   * cannot delete that cookie itself, so without this call "sign out" would
   * leave a valid thirty-day credential in the browser. The backend revokes the
   * whole token family and clears the cookie; it always answers success, so this
   * never blocks the local sign-out.
   */
  logout: () => apiClient.post<null>(`${BASE}/logout`, undefined, { skipRefresh: true }),

  forgotPassword: (body: { email: string }) =>
    apiClient.post<ForgotPasswordResponse>(`${BASE}/forgot-password`, body, { skipAuth: true }),

  resetPassword: (body: { token: string; newPassword: string }) =>
    apiClient.post<null>(`${BASE}/reset-password`, body, { skipAuth: true }),

  /** Authenticated: mints a five-minute token bound to one action class. */
  stepUp: (body: { code: string; action: StepUpAction }) =>
    apiClient.post<StepUpResponse>(`${BASE}/step-up`, body, CODE_ENDPOINT),

  me: () => apiClient.get<ProfileResponse>(`${ACCOUNT}/me`),

  startTotpSetup: () => apiClient.post<TotpSetupResponse>(`${ACCOUNT}/2fa/setup`),

  confirmTotpSetup: (body: { code: string }) =>
    apiClient.post<TotpConfirmedResponse>(`${ACCOUNT}/2fa/confirm`, body, CODE_ENDPOINT),

  disableTotp: (body: { code: string }) =>
    apiClient.post<null>(`${ACCOUNT}/2fa/disable`, body, CODE_ENDPOINT)
};

/**
 * Converts the wire token payload into what the token store wants.
 *
 * The backend speaks ISO-8601; `tokenStore` reasons in Unix seconds so it can
 * compare against `Date.now() / 1000` without re-parsing a string on every
 * request. One conversion, here, at the boundary.
 */
export function toAuthTokens(tokens: AuthTokensResponse): AuthTokens {
  return {
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    expiresAt: toUnixSeconds(tokens.accessTokenExpiresAt)
  };
}

/** TanStack Query keys for the authenticated account surface. */
export const accountKeys = {
  all: ['account'] as const,
  me: () => ['account', 'me'] as const
};
